/**
 * Bản nháp của trình soạn khuôn nghiệp vụ — phần thuần của `components/replica/khuonNhap.ts` và các phép tính rải trong
 * `KhuonTab.tsx`/`SoanKhuon.tsx` của native. Tách ra để kiểm được bằng `node:test`.
 */
import type { CotHienThi, Khuon, KhuonInput, KieuKhuon, ThamSoKhuon } from '../types.ts';

/**
 * Bản nháp đang soạn. `id` rỗng = khuôn chưa lưu lần nào.
 *
 * Ba trường JSON giữ dạng CHUỖI chứ không phải object đã phân tích: người soạn gõ dở thì chuỗi vẫn là chuỗi, còn object
 * thì mỗi phím bấm là một lần phân tích hỏng — và ô nhập sẽ nhảy về rỗng giữa lúc đang gõ.
 */
export interface Nhap {
  id: string;
  nhomId: string;
  ten: string;
  moTa: string;
  cachGoi: string;
  cauHoiMau: string;
  kieu: KieuKhuon;
  sql: string;
  tenThuTuc: string;
  duongDan: string;
  phuongThuc: 'GET' | 'POST';
  thamSoJson: string;
  cotJson: string;
  tongHopJson: string;
  tomTat: string;
  tieuDe: string;
}

export const NHAP_RONG = (nhomId: string, kieu: KieuKhuon = 'cau_lenh'): Nhap => ({
  id: '',
  nhomId,
  ten: '',
  moTa: '',
  cachGoi: '',
  cauHoiMau: '',
  kieu,
  sql: '',
  tenThuTuc: '',
  duongDan: '',
  phuongThuc: 'GET',
  thamSoJson: '[]',
  cotJson: '[]',
  tongHopJson: '{}',
  tomTat: '',
  tieuDe: '',
});

export const inJson = (v: unknown) => JSON.stringify(v ?? null, null, 2);

export const tuKhuon = (k: Khuon): Nhap => ({
  id: k.id,
  nhomId: k.nhomId,
  ten: k.ten,
  moTa: k.moTa,
  cachGoi: k.cachGoi.join(', '),
  cauHoiMau: k.cauHoiMau.join('\n'),
  kieu: k.kieu ?? 'cau_lenh',
  sql: k.sql,
  tenThuTuc: k.tenThuTuc ?? '',
  duongDan: k.duongDan ?? '',
  phuongThuc: k.phuongThuc ?? 'GET',
  thamSoJson: inJson(k.thamSo ?? []),
  cotJson: inJson(k.cot ?? []),
  tongHopJson: inJson(k.tongHop ?? {}),
  tomTat: k.tomTat ?? '',
  tieuDe: k.tieuDe ?? '',
});

/**
 * JSON đã phân tích, hoặc giá trị mặc định kèm lý do. Không ném: người soạn đang gõ dở là chuyện thường, và một ngoại lệ
 * ở đây làm trắng cả màn hình.
 */
export function docJson<T>(chuoi: string, macDinh: T): { gia_tri: T; loi: string } {
  const t = chuoi.trim();
  if (!t) return { gia_tri: macDinh, loi: '' };
  try {
    return { gia_tri: JSON.parse(t) as T, loi: '' };
  } catch (e) {
    return { gia_tri: macDinh, loi: e instanceof Error ? e.message : String(e) };
  }
}

/** "a, b , ,c" → ["a","b","c"]. Mục rỗng bị bỏ, không thành một cách gọi rỗng. */
export const tach = (s: string, dau: string | RegExp) =>
  s
    .split(dau)
    .map((x) => x.trim())
    .filter(Boolean);

/**
 * Thân gửi lên khi lưu. Máy chủ từ chối khuôn mang cả câu lệnh lẫn thủ tục — nên gửi đúng phần của loại đang chọn, thay
 * vì gửi cả hai rồi để nó báo lỗi một thứ người soạn không hề gõ vào.
 */
export function thanLuuKhuon(n: Nhap): KhuonInput {
  return {
    id: n.id || undefined,
    nhomId: n.nhomId,
    ten: n.ten,
    moTa: n.moTa,
    cachGoi: tach(n.cachGoi, ','),
    cauHoiMau: tach(n.cauHoiMau, '\n'),
    kieu: n.kieu,
    sql: n.kieu === 'cau_lenh' ? n.sql : '',
    tenThuTuc: n.kieu === 'thu_tuc' ? n.tenThuTuc.trim() : '',
    duongDan: n.kieu === 'api' ? n.duongDan.trim() : '',
    phuongThuc: n.phuongThuc,
    thamSo: n.kieu === 'cau_lenh' ? [] : docJson<ThamSoKhuon[]>(n.thamSoJson, []).gia_tri,
    cot: docJson<CotHienThi[]>(n.cotJson, []).gia_tri,
    tongHop: docJson<Record<string, string>>(n.tongHopJson, {}).gia_tri,
    tomTat: n.tomTat,
    tieuDe: n.tieuDe,
  };
}

/**
 * Bảng mà lời từ chối nói là BẢN KHAI chưa có. Rỗng nghĩa là lỗi thuộc loại khác.
 *
 * Đọc mã từ chối có cấu trúc chứ không dò chữ trong câu: một phép dò chữ hỏng ở lần đầu tiên có người sửa câu thông báo.
 */
export function bangThieuTuTuChoi(ds: readonly { code: string; bang?: string }[]): string[] {
  const ten = ds
    .filter((r) => r.code === 'COLUMN_NOT_FOUND' || r.code === 'TABLE_NOT_REPLICATED')
    .map((r) => r.bang)
    .filter((x): x is string => !!x);
  return [...new Set(ten)];
}

/**
 * "Câu lệnh đã đổi" so với bản đã lưu. Với khuôn thủ tục nghĩa là đổi THỦ TỤC hoặc DANH SÁCH THAM SỐ — tham số truyền
 * theo vị trí, nên thêm một cái là đổi lời gọi. Khuôn API: đường dẫn, phương thức hoặc tham số. Đổi thì phải chạy thử lại.
 */
export function cauLenhDaDoi(khuon: Khuon | null, nhap: Nhap): boolean {
  if (!khuon) return false;
  const thamSo = docJson<ThamSoKhuon[]>(nhap.thamSoJson, []).gia_tri;
  if (nhap.kieu === 'api') {
    return (
      (khuon.duongDan ?? '') !== nhap.duongDan.trim() ||
      (khuon.phuongThuc ?? 'GET') !== nhap.phuongThuc ||
      JSON.stringify(khuon.thamSo ?? []) !== JSON.stringify(thamSo)
    );
  }
  if (nhap.kieu === 'thu_tuc') {
    return khuon.tenThuTuc !== nhap.tenThuTuc.trim() || JSON.stringify(khuon.thamSo ?? []) !== JSON.stringify(thamSo);
  }
  return khuon.sql !== nhap.sql;
}

/** Bản nháp khác bản đã lưu (khuôn mới luôn tính là có thay đổi). */
export function coThayDoi(khuon: Khuon | null, nhap: Nhap): boolean {
  return !khuon || JSON.stringify(tuKhuon(khuon)) !== JSON.stringify(nhap);
}

/** Đủ điều kiện bấm Lưu: có tên, có nhóm, và có phần chạy được của đúng loại. */
export function duocLuu(nhap: Nhap): boolean {
  return (
    !!nhap.ten.trim() &&
    !!nhap.nhomId &&
    (nhap.kieu === 'api' ? !!nhap.duongDan.trim() : nhap.kieu === 'thu_tuc' ? !!nhap.tenThuTuc.trim() : !!nhap.sql.trim())
  );
}

export type TabSoan = 'nhan_dien' | 'du_lieu' | 'trinh_bay' | 'chay_thu';

/** Tab nào đang còn thứ chặn Lưu — để chấm đỏ trên nhãn tab, người soạn không phải lật từng tab đi tìm. */
export function loiTheoTab(nhap: Nhap): Record<TabSoan, boolean> {
  const loiThamSo = !!docJson(nhap.thamSoJson, []).loi;
  return {
    nhan_dien: !nhap.ten.trim() || !nhap.nhomId,
    du_lieu:
      nhap.kieu === 'api'
        ? !nhap.duongDan.trim() || loiThamSo
        : nhap.kieu === 'thu_tuc'
          ? !nhap.tenThuTuc.trim() || loiThamSo
          : !nhap.sql.trim(),
    trinh_bay: !!docJson(nhap.cotJson, []).loi || !!docJson(nhap.tongHopJson, {}).loi,
    chay_thu: false,
  };
}

/** Cách điền một tham số: trợ lý suy từ câu hỏi, hằng số của khuôn, hay nền tảng điền vai người hỏi. */
export type CachDien = 'tro_li' | 'co_dinh' | 'dang_nhap';

/** `coDinhTam`: dòng người soạn vừa chọn "cố định" mà chưa gõ giá trị — JSON chưa có gì để nhớ lựa chọn ấy. */
export function cachDienCua(x: ThamSoKhuon, coDinhTam: boolean): CachDien {
  return x.nguon === 'dang_nhap' ? 'dang_nhap' : x.giaTri || coDinhTam ? 'co_dinh' : 'tro_li';
}

/** Đổi cách điền: chỉ giữ giá trị khi sang "cố định", chỉ đặt `nguon` khi sang "vai người hỏi". */
export function doiCachDien(x: ThamSoKhuon, c: CachDien): ThamSoKhuon {
  return { ...x, nguon: c === 'dang_nhap' ? 'dang_nhap' : '', giaTri: c === 'co_dinh' ? x.giaTri : '' };
}

/** Ô chèn được vào mẫu tóm tắt: `ky`, `so_dong`, rồi cột hiển thị và cột tổng hợp (không trùng). */
export function oChenTomTat(nhap: Nhap): string[] {
  const cot = docJson<CotHienThi[]>(nhap.cotJson, [])
    .gia_tri.map((c) => c.field)
    .filter(Boolean);
  const tongHop = Object.keys(docJson<Record<string, string>>(nhap.tongHopJson, {}).gia_tri);
  return ['ky', 'so_dong', ...new Set([...cot, ...tongHop])];
}

/** Thêm `{o}` vào cuối mẫu tóm tắt, chèn một dấu cách nếu chữ trước chưa có. */
export function chenVaoTomTat(tomTat: string, o: string): string {
  return `${tomTat}${tomTat && !tomTat.endsWith(' ') ? ' ' : ''}{${o}}`;
}

/** Tham số NỀN TẢNG điền (vai người hỏi, giá trị cố định) và tham số người chạy thử điền — tách hai loại. */
export function tachThamSoChayThu(thamSo: readonly ThamSoKhuon[]): { tuDien: ThamSoKhuon[]; moHoi: ThamSoKhuon[] } {
  return {
    tuDien: thamSo.filter((x) => x.nguon === 'dang_nhap' || !!x.giaTri),
    moHoi: thamSo.filter((x) => x.nguon !== 'dang_nhap' && !x.giaTri),
  };
}
